/**
 * Portal data loading.
 *
 * The portal reads Supabase directly — it has no local database. It is
 * read-only by Row Level Security, not by convention: a manager's token
 * carries the `manager` role, which has SELECT on every table and no INSERT,
 * UPDATE or DELETE policy anywhere. Hiding buttons is presentation; the policy
 * is the actual boundary.
 *
 * The single exception is voiding a subscription, which goes through the
 * `void_subscription` RPC rather than a table write — a SECURITY DEFINER
 * function that checks the caller's role itself and touches exactly one
 * column. Widening the table policy instead would have let a manager rewrite
 * prices and dates from a browser. See supabase/migrations/0003.
 */
import { getSupabase } from '@shared/supabase';
import { dropVoidedPayments } from '@shared/voids';
import type {
  Coach,
  CoachAssignment,
  Freeze,
  Member,
  Payment,
  Subscription,
  WalkIn,
} from '@shared/types';

export interface PortalData {
  members: Member[];
  subscriptions: Subscription[];
  payments: Payment[];
  walkIns: WalkIn[];
  /**
   * Live freezes. Needed to tell a paused member from an active one — without
   * them the frozen count is structurally zero, which reads as "nobody is on a
   * freeze" rather than as a question nobody asked.
   */
  freezes: Freeze[];
  /** class_key -> coach name. */
  coachAssignments: Record<string, string>;
  /**
   * Every coach the academy has named, class coaches and private-only coaches
   * together. The payout report lists them all, including those who earned
   * nothing in the period — a coach missing from a report reads as a bug.
   */
  roster: string[];
  /** Shared config: the payout split, the walk-in price, the gym name. */
  settings: Record<string, string>;
}

/** The three roles. A user with no row in user_roles can read nothing. */
export type PortalRole = 'frontdesk' | 'manager' | 'city_view';

/**
 * The signed-in user's role. The RLS policy on user_roles lets a user select
 * their OWN row and nothing else, so this cannot be used to enumerate staff.
 */
export async function loadUserRole(): Promise<PortalRole | null> {
  const supabase = getSupabase();
  if (!supabase) return null;

  const { data: session } = await supabase.auth.getSession();
  const uid = session.session?.user.id;
  if (!uid) return null;

  const { data, error } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', uid)
    .maybeSingle();

  if (error || !data) return null;
  return data.role as PortalRole;
}

/**
 * Supabase caps a single response (1,000 rows by default), so every table is
 * read in pages. A gym that has been running for years will exceed that on
 * payments long before anything else.
 */
async function fetchAll<T>(table: string): Promise<T[]> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Cloud is not configured.');

  const PAGE = 1000;
  const rows: T[] = [];

  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .is('deleted_at', null)
      // Without an ORDER BY, Postgres may return rows in a different order
      // for each page, so past 1,000 rows a page could repeat one row and skip
      // another — a payment counted twice or not at all. `id` is unique, so
      // the pages are a clean partition of the table.
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1);

    if (error) throw new Error(`${table}: ${error.message}`);
    if (!data || data.length === 0) break;

    rows.push(...(data as T[]));
    if (data.length < PAGE) break;
  }

  return rows;
}

export async function loadPortalData(): Promise<PortalData> {
  const [
    members,
    subscriptions,
    payments,
    walkIns,
    freezes,
    coaches,
    rosterRows,
    appSettings,
  ] = await Promise.all([
    fetchAll<Member>('members'),
    fetchAll<Subscription>('subscriptions'),
    fetchAll<Payment>('payments'),
    fetchAll<WalkIn>('walk_ins'),
    fetchAll<Freeze>('freezes'),
    fetchAll<CoachAssignment>('coach_assignments'),
    fetchAll<Coach>('coaches'),
    fetchAll<{ key: string; value: string }>('app_settings'),
  ]);

  const coachAssignments: Record<string, string> = {};
  for (const c of coaches) {
    if (c.coach_name?.trim()) coachAssignments[c.class_key] = c.coach_name.trim();
  }

  // Class coaches and private-only coaches are one list here: the report cares
  // about people, not about how they happen to be configured.
  const roster = [
    ...new Set([
      ...Object.values(coachAssignments),
      ...rosterRows.map((c) => c.name?.trim()).filter((n): n is string => Boolean(n)),
    ]),
  ].sort((a, b) => a.localeCompare(b, ['ar', 'en']));

  const settings: Record<string, string> = {};
  for (const s of appSettings) settings[s.key] = s.value;

  return {
    members,
    subscriptions,
    // A voided subscription takes its payments out of every figure the portal
    // draws — revenue, outstanding, coach payouts, City View's share. The
    // fetch above already drops the subscription itself; this drops the money
    // that would otherwise be left behind with nothing to attribute it to.
    payments: dropVoidedPayments(payments, subscriptions),
    walkIns,
    freezes,
    coachAssignments,
    roster,
    settings,
  };
}
